<template>
  <v-flex
    v-if="!loading"
    xs8
    offset-xs2
    align-self-center
    justify-center
  >
  <div>
    <v-container fill-height fluid>
    <v-row>
      <v-flex xs3 class="mr-2">
        <Search
          v-model="filters.search"
          :label="$t('SEARCH')"
          prepend-icon="search"
          :delay="500"
        />
      </v-flex>
      <v-spacer></v-spacer>
      <v-btn
        v-if="can('create users')"
        @click="showUserModal(0)"
        color="primary"
        dark
        class="mb-2"
      >{{ $t("NEW") }}
      </v-btn>
    </v-row>
    </v-container>
    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="totalItems"
      :no-data-text="$t('NO_DATA')"
      :no-results-text="$t('NO_DATA')"
      hide-default-footer
      :options.sync="pagination"
      :loading="loading"
      class="elevation-1"
    >
      <template v-slot:item.name="{ item }">
        <router-link :to="{ name: routeName.USER_SHOW, params: { id: item.id } }">
            {{ item.name }}
        </router-link>
      </template>
      <template v-slot:item.surname="{ item }">
        <router-link :to="{ name: routeName.USER_SHOW, params: { id: item.id } }">
            {{ item.surname }}
        </router-link>
      </template>
      <template v-slot:item.updated_at="{ item }">
            {{ item.updated_at | timeFormat }}
      </template>
      <template v-slot:item.actions="{ item }">
            <v-layout row wrap>
              <v-flex class="text-no-wrap">
                <v-btn
                  icon
                  color="blue"
                  v-if="can('edit users')"
                  @click="showUserModal(item.id)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn
                  v-if="(currentUser.user.id != item.id) && can('delete users')"
                  icon
                  color="red"
                  @click="deleteUser(item.id)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
      </template>
    </v-data-table>
    <Paginator
      :total-items="totalItems"
      :page.sync="pagination.page"
      :items-per-page.sync="pagination.itemsPerPage"
      @change="fetchUsers"
    />
  </div>
  </v-flex>
  <Loader v-else/>
</template>
<script>
  import { mapGetters, mapActions } from 'vuex';

  import { usersService } from '../../utils/services/users.service';
  import Search from '../../components/shared/Inputs/Search';
  import Loader from '../../components/shared/Loader';
  import UserModal from '../../components/modals/UserModal';
  import Paginator from '../../components/shared/Paginator';

  export default {
    name: 'UserList',
    components: { Search, Loader, Paginator },
    data() {
      return {
        loading: false,
        items: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [false],
          sortBy: ['name'],
        },
        headers: [
          {
            text: this.$t('fields.id'),
            align: 'left',
            value: 'id',
          },
          { text: this.$t('fields.name'), value: 'name' },
          { text: this.$t('fields.surname'), value: 'surname' },
          { text: this.$t('fields.email'), value: 'email', sortable: false },
          { text: this.$t('fields.phone'), value: 'phone', sortable: false },
          { text: this.$t('fields.updated_at'), value: 'updated_at' },
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'center' },
        ],
        filters: {
          search: null,
        },
      };
    },
    computed: {
      ...mapGetters('user', ['currentUser']),
      requestFilters() {
        const filters = {
          search: this.filters.search,
        };
        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },
    },
    methods: {
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),
      async fetchUsers(forcePage) {
        this.loading = true;
        const { page, itemsPerPage } = this.pagination;
        return usersService.index({
          page: page || forcePage || 1,
          itemsPerPage,
          filters: this.requestFilters,
        })
          .then(response => {
            this.items = response.data.data;
            this.totalItems = response.data.meta.total;
            this.pagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.pagination, forcePage },
            );
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      async showUserModal(id) {
        this.showModal({
          component: UserModal,
            data: {
              stored: {
                id,
              },
          },
          options: {
            title: this.$t('USER'),
            onSave: (
              form,
              error,
            ) => usersService.save(form)
            .then(() => {
              this.hideModal({});
              this.showToast({ type: 'success', message: this.$t('SAVED') });
            })
            .catch(error => {
              this.handleError(error, this.errors);
            })
            .finally(() => {
              this.fetchUsers(this.pagination.page);
            }),
          },
        });
      },
      async deleteUser(id) {
          this.showConfirm({
            onConfirm: () => {
              this.loading = true;
              return usersService.remove(id)
              .then(() => {
                this.showToast({ type: 'success', message: this.$t('DELETED') });
                this.fetchUsers(this.pagination.page);
              })
              .catch(error => {
                this.handleError(error, this.errors);
              })
              .finally(() => {
                this.loading = false;
              });
            },
          });
      },
    },
    async created() {
      await this.fetchUsers(this.pagination.page);
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          this.fetchUsers(1);
        },
      },
      'pagination.sortBy': {
        handler(oldVal, newVal) {
          if (newVal !== oldVal) {
            this.fetchUsers(1);
          }
        },
      },

    },
  };
</script>
